type MoneyLike = {
  amount?: number | null;
  currency?: string | null;
};

type TaxedMoneyLike = {
  gross?: { amount?: number | null } | null;
  net?: { amount?: number | null } | null;
};

type BackendMoneyValue = TaxedMoneyLike | MoneyLike | null | undefined;

export const SALEOR_CHECKOUT_PAGES = {
  CART: "CART",
  CHECKOUT_SHIPPING: "CHECKOUT_SHIPPING",
  CHECKOUT_PAYMENT: "CHECKOUT_PAYMENT",
  ORDER_SUCCESS: "ORDER_SUCCESS",
} as const;

export type SaleorCheckoutPage = (typeof SALEOR_CHECKOUT_PAGES)[keyof typeof SALEOR_CHECKOUT_PAGES];

export type OrderSummaryDiscountLine = {
  name?: string;
  amount: number;
  type?: string | null;
  scope?: string | null;
  voucherCode?: string | null;
  lineIds?: string[] | null;
};

export type DiscountDetailSource = {
  id?: string | null;
  type?: string | null;
  scope?: string | null;
  name?: string | null;
  translatedName?: string | null;
  amount?: MoneyLike | null;
  promotionRuleId?: string | null;
  voucherCode?: string | null;
  lineIds?: Array<string | null> | null;
};

export type TotalDiscountSource = {
  amount?: MoneyLike | null;
  catalogDiscountAmount?: MoneyLike | null;
  orderDiscountAmount?: MoneyLike | null;
  paymentDiscountAmount?: MoneyLike | null;
  bxgyDiscountAmount?: MoneyLike | null;
  voucherDiscountAmount?: MoneyLike | null;
  shippingDiscountAmount?: MoneyLike | null;
  giftWrappingDiscountAmount?: MoneyLike | null;
  vipDiscountAmount?: MoneyLike | null;
  markdownDiscountAmount?: MoneyLike | null;
};

export type SaleorPricedSource = {
  totalPrice?: BackendMoneyValue;
  total?: BackendMoneyValue;
  discountDetails?: DiscountDetailSource[] | null;
  totalDiscount?: TotalDiscountSource | null;
};

export type SaleorLineSource = {
  displayUnitPrice?: BackendMoneyValue;
  displayTotalPrice?: BackendMoneyValue;
  catalogDiscountAmount?: BackendMoneyValue;
  bxgyDiscountAmount?: BackendMoneyValue;
  unitPrice?: BackendMoneyValue;
  totalPrice?: BackendMoneyValue;
};

export type OrderSummaryDiscountLabels = {
  discount?: string;
  catalog?: string;
  order?: string;
  bxgy?: string;
  voucher?: string;
  giftWrapping?: string;
  payment?: string;
  shipping?: string;
  vip?: string;
  markdown?: string;
};

const DISCOUNT_EPSILON = 0.01;

export function getOrderSummaryDiscountLabels(
  staticText?: Record<string, string>,
): OrderSummaryDiscountLabels {
  return {
    discount: staticText?.cart_discount_amount_label,
    catalog: staticText?.cart_catalog_discount_label,
    order: staticText?.cart_order_discount_label,
    bxgy: staticText?.cart_bxgy_discount_label,
    voucher: staticText?.cart_voucher_discount_label,
    giftWrapping: staticText?.cart_gift_packaging_label,
    payment: staticText?.cart_payment_discount_label,
    shipping: staticText?.cart_shipping_label,
    vip: staticText?.cart_vip_discount_label,
    markdown: staticText?.cart_markdown_discount_label,
  };
}

export function getBackendMoneyAmount(value?: BackendMoneyValue): number {
  if (!value) {
    return 0;
  }
  if ("gross" in value && value.gross) {
    return value.gross.amount ?? 0;
  }
  if ("amount" in value) {
    return typeof value.amount === "number" ? value.amount : 0;
  }
  return 0;
}

export function getAppliedGiftWrapDiscountAmount({
  wrappingEnabled,
  appliedDiscountAmount,
  availableDiscountAmount,
}: {
  wrappingEnabled?: boolean;
  appliedDiscountAmount?: BackendMoneyValue;
  availableDiscountAmount?: BackendMoneyValue;
}): number {
  if (!wrappingEnabled) {
    return 0;
  }

  return getBackendMoneyAmount(appliedDiscountAmount ?? availableDiscountAmount);
}

function hasPositiveAmount(amount: number) {
  return amount > DISCOUNT_EPSILON;
}

export function getSaleorDiscountLines(
  totalDiscount?: TotalDiscountSource | null,
  labels?: OrderSummaryDiscountLabels,
): OrderSummaryDiscountLine[] {
  return (
    [
      {
        type: "CATALOG",
        name: labels?.catalog,
        amount: getBackendMoneyAmount(totalDiscount?.catalogDiscountAmount),
      },
      {
        type: "ORDER",
        name: labels?.order,
        amount: getBackendMoneyAmount(totalDiscount?.orderDiscountAmount),
      },
      {
        type: "BXGY",
        name: labels?.bxgy,
        amount: getBackendMoneyAmount(totalDiscount?.bxgyDiscountAmount),
      },
      {
        type: "VOUCHER",
        name: labels?.voucher,
        amount: getBackendMoneyAmount(totalDiscount?.voucherDiscountAmount),
      },
      {
        type: "SHIPPING",
        name: labels?.shipping,
        amount: getBackendMoneyAmount(totalDiscount?.shippingDiscountAmount),
      },
      {
        type: "GIFT_WRAPPING",
        name: labels?.giftWrapping,
        amount: getBackendMoneyAmount(totalDiscount?.giftWrappingDiscountAmount),
      },
      {
        type: "PAYMENT",
        name: labels?.payment,
        amount: getBackendMoneyAmount(totalDiscount?.paymentDiscountAmount),
      },
      {
        type: "VIP",
        name: labels?.vip,
        amount: getBackendMoneyAmount(totalDiscount?.vipDiscountAmount),
      },
      {
        type: "MARKDOWN",
        name: labels?.markdown,
        amount: getBackendMoneyAmount(totalDiscount?.markdownDiscountAmount),
      },
    ] satisfies OrderSummaryDiscountLine[]
  ).filter((line) => hasPositiveAmount(line.amount));
}

export function getSaleorTotalDiscountAmount(totalDiscount?: TotalDiscountSource | null): number {
  return getBackendMoneyAmount(totalDiscount?.amount);
}

export function getSaleorOrderTotalAmount(source?: SaleorPricedSource | null): number {
  return getBackendMoneyAmount(source?.totalPrice ?? source?.total);
}

export function getOrderSummaryFromSaleor(
  source?: SaleorPricedSource | null,
  labels?: OrderSummaryDiscountLabels,
) {
  return {
    discountLines: getSaleorDiscountLines(source?.totalDiscount, labels),
    totalDiscountAmount: getSaleorTotalDiscountAmount(source?.totalDiscount),
    orderTotalAmount: getSaleorOrderTotalAmount(source),
    catalogDiscountAmount: getBackendMoneyAmount(source?.totalDiscount?.catalogDiscountAmount),
    orderDiscountAmount: getBackendMoneyAmount(source?.totalDiscount?.orderDiscountAmount),
    paymentDiscountAmount: getBackendMoneyAmount(source?.totalDiscount?.paymentDiscountAmount),
    bxgyDiscountAmount: getBackendMoneyAmount(source?.totalDiscount?.bxgyDiscountAmount),
    voucherDiscountAmount: getBackendMoneyAmount(source?.totalDiscount?.voucherDiscountAmount),
    shippingDiscountAmount: getBackendMoneyAmount(source?.totalDiscount?.shippingDiscountAmount),
    giftWrappingDiscountAmount: getBackendMoneyAmount(
      source?.totalDiscount?.giftWrappingDiscountAmount,
    ),
    vipDiscountAmount: getBackendMoneyAmount(source?.totalDiscount?.vipDiscountAmount),
    markdownDiscountAmount: getBackendMoneyAmount(source?.totalDiscount?.markdownDiscountAmount),
  };
}

export function getLineDisplayPrices(line?: SaleorLineSource | null) {
  const unitAmount = getBackendMoneyAmount(line?.displayUnitPrice);
  const totalAmount = getBackendMoneyAmount(line?.displayTotalPrice);
  return {
    unitAmount,
    totalAmount,
    catalogDiscountAmount: getBackendMoneyAmount(line?.catalogDiscountAmount),
    bxgyDiscountAmount: getBackendMoneyAmount(line?.bxgyDiscountAmount),
    hasSaleorDisplayPrice: line?.displayUnitPrice != null || line?.displayTotalPrice != null,
  };
}

export function getLineDiscountLines(
  line?: SaleorLineSource | null,
  labels?: OrderSummaryDiscountLabels,
): OrderSummaryDiscountLine[] {
  const lines: OrderSummaryDiscountLine[] = [];
  const catalogAmount = getBackendMoneyAmount(line?.catalogDiscountAmount);
  const bxgyAmount = getBackendMoneyAmount(line?.bxgyDiscountAmount);

  if (hasPositiveAmount(catalogAmount)) {
    lines.push({
      name: labels?.catalog,
      amount: catalogAmount,
      type: "CATALOG",
      scope: "LINE",
    });
  }
  if (hasPositiveAmount(bxgyAmount)) {
    lines.push({
      name: labels?.bxgy,
      amount: bxgyAmount,
      type: "BXGY",
      scope: "LINE",
    });
  }
  return lines;
}

export function getOrderLevelVoucherDetails(discountDetails?: DiscountDetailSource[] | null) {
  return (discountDetails ?? []).filter((detail) => {
    const hasVoucher = Boolean(detail.voucherCode) || detail.type?.toUpperCase() === "VOUCHER";
    if (!hasVoucher) {
      return false;
    }
    const scope = detail.scope?.toUpperCase();
    return scope !== "LINE" && !(detail.lineIds && detail.lineIds.length > 0 && scope !== "ORDER");
  });
}

export function cartEntryPaymentClearPlan() {
  return {
    mutation: "checkoutClearPaymentGateway" as const,
    clearShippingMethod: false,
    oncePerEntry: true,
  };
}

export function resolveCheckoutQueryPage(input: {
  isCart?: boolean;
  isPayment?: boolean;
  isOrderSuccess?: boolean;
}): SaleorCheckoutPage {
  if (input.isOrderSuccess) {
    return SALEOR_CHECKOUT_PAGES.ORDER_SUCCESS;
  }
  if (input.isCart) {
    return SALEOR_CHECKOUT_PAGES.CART;
  }
  if (input.isPayment) {
    return SALEOR_CHECKOUT_PAGES.CHECKOUT_PAYMENT;
  }
  return SALEOR_CHECKOUT_PAGES.CHECKOUT_SHIPPING;
}
